import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext, useParams } from "react-router-dom";
import { IconButton, Paper, Portal, TaskAddIcon, Tooltip } from "@activecollab/components";
import ErrorBoundary from "~/components/ErrorBoundry";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener";
import { MyGroupedByPicker } from "~/components/MyWork/MyTasks/MyGroupedByPicker";
import UserTasksList from "~/components/MyWork/UserTasksList";
import { VerticalSeparator } from "~/components/project/Header/VerticalSeparator";
import { useCreateTask } from "~/components/Providers/CreateTask";
import { useCreateTaskContext } from "~/components/Providers/CreateTask/context";
import { FETCH_USER_TASKS_SUCCESS } from "~/store/action_types";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { fetchUserTasks } from "~/store/actions/actions.tasks";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getUserHaveAssignments } from "~/store/selectors/selector.userTasks";
import UserProfileListLoader from "./UserProfileListLoader";

export const USER_PROFILE_TASK_PAGE = "user_profile_task_page";

const UserProfileTasksPage = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { controlsEl, userProjectsData } = useOutletContext();
  const dispatch = useDispatch();
  const { openTaskForm } = useCreateTask();
  const { setModalOpenFrom } = useCreateTaskContext();
  const [isLoading, setIsLoading] = useState(true);
  const groupedBy = useSelector((state) => {
    const groupBy = getRawPersonalizedConfigOption(state, `user_profile_tasks_group_by`);
    return groupBy ?? "project";
  });

  const handleAddNewTask = useCallback(() => {
    openTaskForm(false, +userId);
    setModalOpenFrom(USER_PROFILE_TASK_PAGE);
  }, [openTaskForm, setModalOpenFrom, userId]);

  const handleChangeGroupedBy = useCallback(
    (group) => {
      dispatch(
        editPersonalizedConfigOptions({
          user_profile_tasks_group_by: group,
        })
      );
    },
    [dispatch]
  );

  const { data } = userProjectsData;
  const projectsList = useMemo(
    () => data?.pages.flatMap((page) => page.projects).filter((project) => !project.is_completed),
    [data?.pages]
  );

  useEffect(() => {
    dispatch(fetchUserTasks(+userId)).then((response) => {
      if (response.type === FETCH_USER_TASKS_SUCCESS) {
        setIsLoading(false);
      }
    });
  }, [dispatch, userId]);

  const haveAssignments = useSelector((state) => getUserHaveAssignments(state, +userId));

  const refetch = useCallback(
    (task) => {
      if (task.assignee_id === +userId) {
        dispatch(fetchUserTasks(+userId)).then((response) => {
          if (response.type === FETCH_USER_TASKS_SUCCESS) {
            setIsLoading(false);
          }
        });
      }
    },
    [dispatch, userId]
  );

  useGlobalPrivateChannelListener("TaskCreated", refetch);

  return isLoading ? (
    <Paper
      className="tw-flex-1 tw-mb-8 tw-relative"
      style={{ minWidth: "350px" }}
      type="paper-2"
    >
      <UserProfileListLoader itemsNumber={6} />
    </Paper>
  ) : (
    <ErrorBoundary errorLocation="UserProfileTasksPage">
      <UserTasksList userId={+userId} />
      {controlsEl ? (
        <Portal container={controlsEl}>
          {projectsList?.length ? (
            <>
              {haveAssignments ? (
                <>
                  <MyGroupedByPicker
                    groupedBy={groupedBy}
                    onChange={handleChangeGroupedBy}
                  />
                  <VerticalSeparator className="tw-mx-3" />
                </>
              ) : null}
              <Tooltip title={t("Add New Task")}>
                <IconButton
                  variant="contained"
                  onClick={handleAddNewTask}
                  size="big"
                >
                  <TaskAddIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </Portal>
      ) : null}
    </ErrorBoundary>
  );
};

export default UserProfileTasksPage;
